import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI, bodyAPI, agendaAPI, userAPI, noticeAPI, aiAPI, organizationAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft, Send, Loader2, Sparkles, Users, Mail,
  CheckCircle, Clock, FileText, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const NoticesPage = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [body, setBody] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [noticeLogs, setNoticeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchData();
  }, [meetingId]);

  const fetchData = async () => {
    try {
      const [meetingRes, usersRes, orgRes] = await Promise.all([
        meetingAPI.get(meetingId),
        userAPI.getAll(),
        user?.organization_id ? organizationAPI.get(user.organization_id) : Promise.resolve({ data: null }),
      ]);

      setMeeting(meetingRes.data);
      setOrgUsers(usersRes.data);
      setOrganization(orgRes.data);

      const mtg = meetingRes.data;
      setSubject(`Notice of ${mtg.title}`);

      if (mtg.body_id) {
        const bodyRes = await bodyAPI.get(mtg.body_id);
        setBody(bodyRes.data);
      }

      const [agendaRes, logRes] = await Promise.all([
        agendaAPI.getByMeeting(meetingId),
        noticeAPI.getByMeeting(meetingId),
      ]);

      setAgendaItems(agendaRes.data);
      setNoticeLogs(logRes.data);

      // Build default notice body
      const dateStr = mtg.date_time ? format(parseISO(mtg.date_time), 'MMMM d, yyyy \'at\' h:mm a') : '___';
      const venue = mtg.venue || mtg.virtual_link || 'To be announced';
      const agendaList = agendaRes.data.length > 0
        ? agendaRes.data.map((item, i) => `${i + 1}. ${item.title}`).join('\n')
        : 'To be announced';
      
      setBodyHtml(`Dear Member,\n\nPlease be advised that a meeting of the ${bodyRes?.data?.name || mtg.meeting_type} will be held on ${dateStr} at ${venue}.\n\nAGENDA:\n${agendaList}\n\nYour presence is requested.\n\nRespectfully,\n${user?.full_name || 'Corporate Secretary'}`);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const bodyRes = { data: body };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    setSelectedRecipients(checked ? orgUsers.map(u => u.id) : []);
  };

  const toggleRecipient = (userId) => {
    setSelectedRecipients(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSend = async () => {
    if (selectedRecipients.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    setSending(true);
    try {
      const result = await noticeAPI.send({
        meeting_id: meetingId,
        recipient_ids: selectedRecipients,
        subject,
        body_html: bodyHtml,
      });
      toast.success(`Notice sent to ${result.data.notices_sent} recipients`);
      // Refresh logs
      const logRes = await noticeAPI.getByMeeting(meetingId);
      setNoticeLogs(logRes.data);
      setSelectedRecipients([]);
      setSelectAll(false);
    } catch (err) {
      toast.error('Failed to send notice');
    } finally {
      setSending(false);
    }
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const response = await aiAPI.draftNotice({ meeting_id: meetingId });
      if (response.data?.draft) {
        setBodyHtml(response.data.draft);
        toast.success('AI notice draft generated');
      }
    } catch (err) {
      toast.error('AI generation failed');
    } finally {
      setGeneratingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  const aiEnabled = organization?.ai_enabled && organization?.ai_notices_enabled !== false;

  return (
    <div className="space-y-6" data-testid="notices-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/meetings/${meetingId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-[#1A1D21]">Meeting Notice</h1>
          <p className="text-sm text-[#5A6570]">{meeting?.title}</p>
        </div>
      </div>

      <Tabs defaultValue="compose" className="space-y-4">
        <TabsList className="bg-[#F0F2F4]">
          <TabsTrigger value="compose" className="data-[state=active]:bg-white">
            <Mail className="h-4 w-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="logs" className="data-[state=active]:bg-white">
            <Clock className="h-4 w-4 mr-2" />
            Sent Log ({noticeLogs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="space-y-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Notice Compose */}
            <div className="lg:col-span-2 space-y-4">
              <Card className="border-[#E2E6EA]">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Notice Content</CardTitle>
                    {aiEnabled && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateAI}
                        disabled={generatingAI}
                        className="border-[#00A3A3] text-[#00A3A3]"
                        data-testid="ai-draft-notice"
                      >
                        {generatingAI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                        Draft with AI
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Subject</Label>
                    <Input
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="border-[#E2E6EA]"
                      data-testid="notice-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Body</Label>
                    <Textarea
                      value={bodyHtml}
                      onChange={(e) => setBodyHtml(e.target.value)}
                      className="min-h-[300px] border-[#E2E6EA] text-sm font-mono"
                      data-testid="notice-body"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recipients */}
            <div className="space-y-4">
              <Card className="border-[#E2E6EA]">
                <CardHeader>
                  <CardTitle className="text-base">Recipients</CardTitle>
                  <CardDescription>{selectedRecipients.length} of {orgUsers.length} selected</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2 pb-3 border-b border-[#E2E6EA]">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-recipients"
                    />
                    <Label className="text-sm cursor-pointer">Select All</Label>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {orgUsers.map(u => (
                      <div key={u.id} className="flex items-center gap-2 py-1">
                        <Checkbox
                          checked={selectedRecipients.includes(u.id)}
                          onCheckedChange={() => toggleRecipient(u.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1A1D21] truncate">{u.full_name}</p>
                          <p className="text-xs text-[#5A6570] truncate">{u.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Button
                onClick={handleSend}
                disabled={sending || selectedRecipients.length === 0}
                className="w-full bg-[#F58A26] hover:bg-[#E07A1C]"
                data-testid="send-notice-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send Notice ({selectedRecipients.length})
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          {noticeLogs.length === 0 ? (
            <Card className="border-[#E2E6EA]">
              <CardContent className="py-12 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-[#A8B1B9]" />
                <h3 className="text-lg font-medium text-[#1A1D21]">No notices sent yet</h3>
                <p className="text-[#5A6570]">Compose and send a notice to recipients</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {noticeLogs.map(log => (
                <Card key={log.id} className="border-[#E2E6EA]">
                  <CardContent className="p-4 flex items-center gap-4">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-[#1A1D21]">{log.recipient_name}</p>
                      <p className="text-xs text-[#5A6570]">{log.recipient_email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-[#5A6570]">{log.mode}</p>
                      <p className="text-xs text-[#A8B1B9]">{log.sent_at ? format(parseISO(log.sent_at), 'MMM d, h:mm a') : ''}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NoticesPage;
